import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Checkbox } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";
import EditorBoxColumn, { ChangeHandler } from "react-lib/appcon/common/EditorBoxColumn";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import { TreeNode } from "react-lib/apps/common/TreeViewSearchBox";

import { mapOptions } from "react-lib/apps/HISV3/common/CommonInterface";

import CardTransferList from "react-lib/apps/USER/CardTransferList";

import CardDivisionSpecialPermissionUX from "./CardDivisionSpecialPermissionUX";

import { getDivisionDescendants } from "../common/sequence/AppInitialize";
import {
  ACTIONS,
  ADMIN_MENU_OPTIONS,
  BTN_ACTS,
  MasterOptions,
  PickedState,
  RunSequence,
  SetProp,
  State,
} from "./sequence/DSPermission";

import { getUnlinkedNonOrgDivisions, ORGANIZE_LABEL } from "./HRMInterface";

import CONFIG from "config/config";

type CardDivisionSpecialPermissionProps = Pick<
  PickedState,
  "buttonLoadCheck" | "selectedEmployee"
> & {
  setProp: SetProp;
  runSequence: RunSequence;
  DSPermissionSequence: State["DSPermissionSequence"];
  readOnly?: boolean;
  allDivisionGroup: Record<string, any>[];
  masterOptions: MasterOptions;
};

const CardDivisionSpecialPermission = (props: CardDivisionSpecialPermissionProps) => {
  const [affiliateDict, setAffiliateDict] = useState<Record<string, TreeNode[]>>({});
  const [modConfirmRemove, setModConfirmRemove] = useState<number | null>(null);

  useEffect(() => {
    props.runSequence({ sequence: "DSPermission", restart: true });
  }, []);

  useEffect(() => {
    const dict = props.DSPermissionSequence?.affiliateDict ?? {};

    setAffiliateDict(dict as Record<string, TreeNode[]>);
  }, [props.DSPermissionSequence?.affiliateDict]);

  const handleChangeTreeview = useCallback(
    (index: number): ChangeHandler =>
      async (e, { value }) => {
        props.runSequence({
          sequence: "DSPermission",
          action: ACTIONS.CHANGE_TREE_VIEW,
          index,
          value,
        });
      },
    [props.DSPermissionSequence?.patientOpDivisions]
  );

  const handleChangeValue = useCallback(
    (index: number) => async (e: SyntheticEvent | null, data: any) => {
      const value = data.type === "checkbox" ? data.checked : data.value;

      props.runSequence({
        sequence: "DSPermission",
        action: ACTIONS.CHANGE_VALUE,
        index,
        name: data.name,
        value,
      });
    },
    [props.DSPermissionSequence?.patientOpDivisions]
  );

  const handleParentSearchTreeview = useCallback((data: any) => {
    props.runSequence({
      sequence: "DSPermission",
      action: ACTIONS.AFFILIATE_SEARCH,
      ...data,
    });
  }, []);

  const handleChangeDivision = useCallback(
    (index: number): ChangeHandler =>
      async (e, data) => {
        const value = (data.value || []) as number[];

        props.runSequence({
          sequence: "DSPermission",
          action: ACTIONS.CHANGE_DIVISION,
          index,
          value,
        });
      },
    []
  );

  const handleSearchTreeview = useCallback((data: any) => {
    props.runSequence({
      sequence: "DSPermission",
      action: ACTIONS.AFFILIATE_SEARCH,
      ...data,
    });
  }, []);

  const getDivisions = useCallback(
    (value?: number | null) => {
      const divisions = getDivisionDescendants(value, props.allDivisionGroup);
      const unlinkedDivisions = mapOptions(getUnlinkedNonOrgDivisions(props.allDivisionGroup));
      const filteredDivisions = mapOptions(
        divisions.filter((div) => div.type_label !== ORGANIZE_LABEL)
      );

      const linkedDivisionOptions =
        filteredDivisions.length === 0
          ? []
          : [
              {
                key: "ALL",
                subOptions: filteredDivisions,
                text: "ALL",
                value: "ALL",
              },
            ];

      return value
        ? linkedDivisionOptions
        : [
            {
              key: "ALL",
              subOptions: unlinkedDivisions,
              text: "ALL",
              value: "ALL",
            },
          ];
    },
    [props.allDivisionGroup]
  );

  const filteredAdminMenuList = useMemo(
    () =>
      ADMIN_MENU_OPTIONS.filter((option) => {
        const hiddenTabs: string[] = CONFIG.V3_HIDDEN_TAB_LIST || [];

        return !hiddenTabs.includes(option.value);
      }),
    []
  );

  const fullName = useMemo(() => {
    const { masterOptions, selectedEmployee } = props;

    const fullNameTh = selectedEmployee?.full_name || "";

    const foundPrename = masterOptions.prenameEn?.find(
      (option) => Number(option.value) === selectedEmployee?.pre_name_en
    );

    const prename = foundPrename?.text || "";
    const fullNameEn = `${prename} ${selectedEmployee?.first_name_en || ""} ${
      selectedEmployee?.last_name_en || ""
    }`;

    return `${fullNameTh} ${fullNameEn}`;
  }, [props.selectedEmployee]);

  const affiliateName = useMemo(() => {
    const affiliate: Record<string, any>[] = props.selectedEmployee?.affiliate || [];

    if (!Array.isArray(affiliate) || affiliate.length === 0) {
      return "-";
    }

    return affiliate.map((item) => item.name as string).join(", ");
  }, [props.selectedEmployee?.affiliate]);

  const formattedItems = useMemo(
    () =>
      (props.DSPermissionSequence?.patientOpDivisions ?? []).map((item, index) => {
        const divisions = getDivisions(item.parentId);

        return {
          ...item,
          action: (
            <div style={{ display: "grid", placeContent: "center" }}>
              <Button
                style={{ fontSize: "0.65rem" }}
                color="red"
                disabled={props.readOnly}
                icon="minus"
                size="mini"
                circular
                onClick={() => {
                  setModConfirmRemove(index);
                }}
              />
            </div>
          ),
          active: (
            <div style={{ display: "grid", placeContent: "center" }}>
              <Checkbox
                checked={item.is_Active}
                disabled={props.readOnly}
                name="is_Active"
                onChange={handleChangeValue(index)}
              />
            </div>
          ),
          adminMenu: (
            <DropdownOptions
              disabled={props.readOnly}
              menuStyle={{ maxHeight: "calc(100vh - 42.75rem)" }}
              name="adminMenuList"
              value={item.adminMenuList}
              checked
              clearable
              multiple
              search
              searchBox
              options={filteredAdminMenuList}
              showSubOptions
              onChange={handleChangeValue(index)}
            />
          ),
          affiliate: (
            <EditorBoxColumn
              id={item.rowIndex}
              boxType="treeview"
              disabled={props.readOnly}
              items={affiliateDict[item.rowIndex]}
              value={item.parentName}
              editing
              onChange={handleChangeTreeview(index)}
              onParentSearch={handleParentSearchTreeview}
              onSearch={handleSearchTreeview}
            />
          ),
          division: (
            <DropdownOptions
              disabled={props.readOnly}
              name="divisions"
              value={item.divisions.filter((div) => div.active).map((div) => div.id)}
              checked
              clearable
              multiple
              search
              searchBox
              searchChildren
              options={divisions}
              showSubOptions
              onChange={handleChangeDivision(index)}
            />
          ),
          patient: (
            <div style={{ display: "grid", placeContent: "center" }}>
              <Checkbox
                checked={item.isAccessPatient}
                disabled={props.readOnly}
                name="isAccessPatient"
                onChange={handleChangeValue(index)}
              />
            </div>
          ),
        };
      }),
    [
      affiliateDict,
      handleChangeTreeview,
      handleChangeValue,
      handleSearchTreeview,
      props.allDivisionGroup,
      props.DSPermissionSequence?.patientOpDivisions,
      props.masterOptions.division,
      props.readOnly,
    ]
  );

  const handleGetTdProps = () => ({
    style: {
      overflow: "unset",
    },
  });

  const handleSelectRoles = (selected: (number | string)[]) => {
    props.setProp("DSPermissionSequence.roles", selected.map(Number));
  };

  const handleSave = () => {
    props.runSequence({ sequence: "DSPermission", action: ACTIONS.SAVE });
  };

  const handleClickAdd = () => {
    props.runSequence({ sequence: "DSPermission", action: ACTIONS.ADD });
  };

  const handleCloseModConfirm = () => {
    setModConfirmRemove(null);
  };

  const handleRemove = () => {
    if (typeof modConfirmRemove === "number") {
      props.runSequence({
        sequence: "DSPermission",
        action: ACTIONS.REMOVE,
        index: modConfirmRemove,
        onSuccess: handleCloseModConfirm,
      });
    }
  };

  console.log("CardDivisionSpecialPermission props:", props);

  return (
    <div
      style={{
        height: "calc(100vw - 61rem)",
        marginTop: "-1.5rem",
        overflow: "auto",
      }}
    >
      <CardDivisionSpecialPermissionUX
        affiliateName={affiliateName}
        fullName={fullName}
        readOnly={props.readOnly}
        tableData={formattedItems}
        getTdProps={handleGetTdProps}
        onClickAdd={handleClickAdd}
        buttonSave={
          !props.readOnly && (
            <ButtonLoadCheck
              setProp={props.setProp}
              style={{ margin: 0 }}
              color="green"
              paramKey={BTN_ACTS.SAVE}
              size="small"
              title="SAVE"
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
              onClick={handleSave}
            />
          )
        }
        rolePermission={
          <CardTransferList
            disabled={props.readOnly}
            list={props.masterOptions.role}
            selected={props.DSPermissionSequence?.roles}
            showRightOnly={props.readOnly}
            textKey="text"
            valueKey="value"
            onSelected={handleSelectRoles}
          />
        }
      />

      <ModConfirmCNMI
        approveButtonLoading={props.buttonLoadCheck?.[BTN_ACTS.REMOVE]}
        approveButtonText="Confirm"
        denyButtonText="Cancel"
        openModal={typeof modConfirmRemove === "number"}
        titleColor="red"
        titleName="Alert message"
        onApprove={handleRemove}
        onCloseWithDimmerClick={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        content={
          <div
            style={{
              fontWeight: "bold",
              lineHeight: 2,
              margin: "-0.5rem 0 -1.5rem",
              textAlign: "center",
            }}
          >
            <div>Are you sure want to delete this item?</div>
          </div>
        }
      />
    </div>
  );
};

CardDivisionSpecialPermission.displayName = "CardDivisionSpecialPermission";

export default React.memo(CardDivisionSpecialPermission);
